import { ConsentService } from '../consent/ConsentService';
import { GA4Adapter } from './adapters/GA4Adapter';
import { MetaCapiAdapter } from './adapters/MetaCapiAdapter';
import { MetaPixelAdapter } from './adapters/MetaPixelAdapter';
import { EventDeduplicator } from './eventDeduplicator';
import { AnalyticsAdapter, AnalyticsConfig, AnalyticsEventParams, AnalyticsUser, XegueiEventName } from './types';

declare global {
  interface Window {
    xegueiAnalyticsDebug?: {
      lastEvents: any[];
      getConfig: () => AnalyticsConfig | undefined;
      flush: () => void;
    };
  }
}

type QueuedEvent = {
  type: 'track' | 'page';
  event: XegueiEventName;
  params: AnalyticsEventParams;
  user?: AnalyticsUser;
};

/**
 * AnalyticsService — orquestrador central.
 *
 * Responsabilidades:
 *  - Inicializar adapters (Pixel/CAPI/GA4) APENAS após consentimento + config ativa
 *  - Bufferar eventos pré-init (queue)
 *  - Deduplicar via EventDeduplicator (Pixel ↔ CAPI usam o MESMO eventId)
 *  - Respeitar flags rastrearProdutos/rastrearPedidos/rastrearWhatsapp/rastrearAbandono
 *  - Modo debug: window.xegueiAnalyticsDebug
 *
 * Não é Singleton de módulo — instância única é provida pelo AnalyticsProvider.
 */
export class AnalyticsService {
  private config?: AnalyticsConfig;
  private user?: AnalyticsUser;
  private adapters: AnalyticsAdapter[] = [];
  private initialized = false;
  private queue: QueuedEvent[] = [];
  private debugLog: any[] = [];

  constructor() {
    this.adapters = [
      new MetaPixelAdapter(),
      new GA4Adapter(),
      new MetaCapiAdapter(),
    ];
    if (typeof window !== 'undefined') {
      window.xegueiAnalyticsDebug = {
        lastEvents: this.debugLog,
        getConfig: () => this.config,
        flush: () => {
          this.debugLog.length = 0;
        },
      };
    }
  }

  /** Carrega configuração da empresa (chamado pelo provider). */
  async setConfig(config: AnalyticsConfig | undefined): Promise<void> {
    this.config = config;
    await this.maybeInitialize();
  }

  setUser(user: AnalyticsUser | undefined): void {
    this.user = user;
  }

  /** Reage a mudança de consentimento. */
  async onConsentChanged(): Promise<void> {
    await this.maybeInitialize();
  }

  private podeInicializar(): boolean {
    const c = this.config;
    if (!c) return false;
    // Considera ativo se qualquer integração estiver ligada (Meta Ads OU Analytics)
    const metaAds = c.metaAdsAtivo === true || (c.metaAdsAtivo === undefined && c.ativo === true);
    const analytics = c.analyticsAtivo === true || (c.analyticsAtivo === undefined && c.ativo === true);
    if (!metaAds && !analytics) return false;
    if (c.exigirConsentimento !== false && !ConsentService.hasConsent()) return false;
    const temMetaPixel = metaAds && (!!c.metaPixelId || !!c.capiHabilitado);
    const temGa4 = analytics && !!c.ga4MeasurementId;
    if (!temMetaPixel && !temGa4) return false;
    return true;
  }

  private async maybeInitialize(): Promise<void> {
    if (this.initialized) return;
    if (!this.podeInicializar() || !this.config) return;

    await Promise.all(
      this.adapters.map(async (a) => {
        try {
          await a.initialize(this.config!);
        } catch (err) {
          console.warn(`[Analytics] init adapter ${a.name} falhou`, err);
        }
      })
    );
    this.initialized = true;
    this.flushQueue();
  }

  private flushQueue(): void {
    if (!this.queue.length) return;
    const pending = this.queue.slice();
    this.queue.length = 0;
    pending.forEach((q) => this.dispatch(q.event, q.params, q.user));
  }

  /** Verifica flags por evento. */
  private permitido(event: XegueiEventName): boolean {
    const c = this.config;
    if (!c) return false;
    if (c.rastreamentoAutomatico === false && event === 'PageView') return false;
    if (c.rastrearProdutos === false && event === 'ViewContent') return false;
    if (c.rastrearWhatsapp === false && event === 'Lead') return false;
    if (c.rastrearAbandono === false && event === 'AbandonCheckout') return false;
    if (c.rastrearPedidos === false && event === 'Purchase') return false;
    return true;
  }

  /**
   * Dispara evento. Garante:
   *  - dedup
   *  - eventId compartilhado entre adapters
   *  - filas pré-init
   *  - logging em debug
   */
  track(event: XegueiEventName, params: AnalyticsEventParams = {}, user?: AnalyticsUser): void {
    const effectiveUser = user || this.user;

    if (!this.permitido(event)) return;

    // Garante eventId único e estável entre adapters
    const eventId = params.eventId || generateEventId(event);
    const eventSourceUrl = params.eventSourceUrl || (typeof window !== 'undefined' ? window.location.href : undefined);
    const finalParams: AnalyticsEventParams = { ...params, eventId, eventSourceUrl };

    // Dedup
    const dedupKey = `${eventId}`;
    if (!EventDeduplicator.shouldDispatch(event, dedupKey)) return;

    if (!this.initialized) {
      // Tenta inicializar (pode estar aguardando config/consent)
      this.maybeInitialize();
      this.queue.push({ type: 'track', event, params: finalParams, user: effectiveUser });
      return;
    }

    this.dispatch(event, finalParams, effectiveUser);
  }

  page(params: AnalyticsEventParams = {}): void {
    this.track('PageView', params);
  }

  private dispatch(event: XegueiEventName, params: AnalyticsEventParams, user?: AnalyticsUser): void {
    if (this.config && this.config.debug) {
      const entry = { ts: new Date().toISOString(), event, params, user: user ? { externalId: user.externalId } : undefined };
      this.debugLog.push(entry);
      if (this.debugLog.length > 50) this.debugLog.shift();
      // eslint-disable-next-line no-console
      console.log('[xegueiAnalytics]', entry);
    }
    this.adapters.forEach((a) => {
      if (!a.isReady()) return;
      try {
        a.track(event, params, user);
      } catch (err) {
        console.warn(`[Analytics] adapter ${a.name} track falhou`, err);
      }
    });
  }
}

function generateEventId(event: string): string {
  // UUID v4 simples (sem dependência externa)
  const base = `${event}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return base;
}
